import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useProduct } from "@/components/pdp/PdpDataContext";

// PDP 3.4 — Ingredients section (Shopify: sections/pdp34-ingredients.liquid).
// 3 tabs: Ingredient Spotlight (default, 6-item swipe row) · Formulated Without
// (4-col static grid, same cell system) · Full List (centered text).
// All copy lives in data/pdp-3-4/content.js → `actives`.

const TabNav = ({ tabs, active, onChange }) => (
  <div role="tablist" aria-label="Ingredients" className="ing-tabs" data-testid="ingredients-tabs">
    {tabs.map((t) => {
      const isActive = t.id === active;
      return (
        <button
          key={t.id}
          role="tab"
          type="button"
          aria-selected={isActive}
          data-state={isActive ? "active" : "inactive"}
          data-testid={`ingredients-tab-${t.id}`}
          onClick={() => onChange(t.id)}
          className="ing-tab"
        >
          {t.label}
          {isActive && <motion.span layoutId="ing-tab-underline" className="ing-tab-line" transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} />}
        </button>
      );
    })}
  </div>
);

const Cell = ({ image, icon, title, text, testId }) => (
  <li className={`ing-cell ${icon ? "ing-cell--icon" : ""}`} data-testid={testId}>
    {image && (
      <span className="ing-thumb">
        <img src={image} alt={title} loading="lazy" />
      </span>
    )}
    {icon && <img className="ing-icon" src={icon} alt="" aria-hidden="true" loading="lazy" />}
    <h3 className="ing-cell-title">{title}</h3>
    {text && <p className="ing-cell-desc">{text}</p>}
  </li>
);

const SpotlightPanel = ({ a }) => {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };
  useEffect(() => {
    update();
    const el = trackRef.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el?.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, []);

  const step = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const cell = el.querySelector(".ing-cell");
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    el.scrollBy({ left: dir * ((cell?.offsetWidth || 260) + gap), behavior: "smooth" });
  };

  // Desktop mouse drag-to-swipe (touch uses native scroll; Lenis is told to leave the track alone).
  const drag = useRef(null);
  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse") return;
    const el = trackRef.current;
    drag.current = { x: e.clientX, left: el.scrollLeft, moved: false };
    el.style.scrollSnapType = "none";
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    trackRef.current.scrollLeft = drag.current.left - dx;
  };
  const endDrag = () => {
    if (!drag.current) return;
    const el = trackRef.current;
    drag.current = null;
    el.style.scrollSnapType = "";
    const cell = el.querySelector(".ing-cell");
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    const unit = (cell?.offsetWidth || 260) + gap;
    el.scrollTo({ left: Math.round(el.scrollLeft / unit) * unit, behavior: "smooth" });
  };

  return (
    <div className="ing-carousel" data-testid="ingredients-panel-spotlight" data-can-prev={canPrev} data-can-next={canNext}>
      <button type="button" className="ing-arrow ing-arrow--prev" onClick={() => step(-1)} disabled={!canPrev} aria-label="Previous ingredients" data-testid="ingredients-prev">
        <ChevronLeft size={20} strokeWidth={1.5} />
      </button>
      <ul
        className="ing-track"
        ref={trackRef}
        data-testid="ingredients-track"
        data-lenis-prevent
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onDragStart={(e) => e.preventDefault()}
      >
        {a.items.map((item, i) => (
          <Cell key={item.name} image={item.image} title={item.name} text={item.benefit} testId={`ingredient-item-${i}`} />
        ))}
      </ul>
      <button type="button" className="ing-arrow ing-arrow--next" onClick={() => step(1)} disabled={!canNext} aria-label="Next ingredients" data-testid="ingredients-next">
        <ChevronRight size={20} strokeWidth={1.5} />
      </button>
    </div>
  );
};

const WithoutPanel = ({ a }) => (
  <ul className="ing-grid" data-testid="ingredients-panel-without">
    {a.formulatedWithout.map((f, i) => (
      <Cell key={f.title} icon={f.icon} title={f.title} text={f.text} testId={`formulated-without-${i}`} />
    ))}
  </ul>
);

const FullListPanel = ({ a }) => (
  <div className="ing-full" data-testid="ingredients-panel-full">
    <p className="ing-full-list" data-testid="ingredients-full-list">{a.fullList}</p>
    <p className="ing-full-without">{a.formulatedWithoutLine}</p>
    <p className="ing-full-disclaimer">{a.fullListDisclaimer}</p>
  </div>
);

const PANELS = { spotlight: SpotlightPanel, without: WithoutPanel, full: FullListPanel };

export const IngredientsTabs = () => {
  const { actives: a } = useProduct();
  const [tab, setTab] = useState(a.tabs[0].id);
  const Panel = PANELS[tab];

  return (
    <section className="bg-white" data-testid="actives-section">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="ing-panel" data-testid="ingredients-section">
          <header className="ing-header">
            <div className="ing-label" data-testid="ingredients-label">{a.eyebrow}</div>
            <h2 className="ing-headline" data-testid="ingredients-headline">{a.titleLines.join(" ")}</h2>
            <TabNav tabs={a.tabs} active={tab} onChange={setTab} />
          </header>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={tab}
              role="tabpanel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="ing-content"
            >
              <Panel a={a} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default IngredientsTabs;
